import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Space,
  Tag,
  Select,
  Button,
  message,
  Row,
  Col,
  Statistic,
  InputNumber,
  Collapse,
  Typography,
  Modal,
  Descriptions,
  Spin,
  Input,
  Tabs,
  Divider,
  Switch,
  Slider,
} from 'antd';
import {
  SearchOutlined,
  DollarOutlined,
  FilterOutlined,
  WarningOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  ReloadOutlined,
  BankOutlined,
  FileTextOutlined,
  ExclamationCircleOutlined,
  PieChartOutlined,
} from '@ant-design/icons';
import PGFNDashboardBI from '../components/PGFNDashboardBI';
import axios from 'axios';
import { API_SERVIDOR2 } from '../config/api';

const { Text, Title } = Typography;
const { Search } = Input;

interface Lead {
  cnpj_basico: string;
  cnpj_completo: string;
  nome_devedor: string;
  total_dividas: number;
  valor_total: number;
  tipos_divida: string[];
  tem_fgts: boolean;
  tem_prev: boolean;
  tem_sida: boolean;
  qtd_ajuizadas: number;
  qtd_nao_ajuizadas: number;
  alguma_ajuizada: boolean;
  todas_ajuizadas: boolean;
  tempo_divida_mais_antiga_anos: number;
  tempo_divida_mais_recente_anos: number;
  uf: string;
  ufs: string[];
  faixa_valor: string;
  faixa_tempo: string;
  score_risco: number;
  linha: string;
}

interface Divida {
  id: string;
  tipo_divida: string;
  cnpj_cpf: string;
  cnpj_basico: string;
  nome_devedor: string;
  tipo_pessoa: string;
  tipo_devedor: string;
  uf_devedor: string;
  numero_inscricao: string;
  situacao_inscricao: string;
  indicador_ajuizado: string;
  data_inscricao: string;
  receita_principal: string;
  valor_consolidado: number;
  faixa_valor: string;
  ano_inscricao: number;
  tempo_inscricao_anos: number;
  faixa_tempo: string;
}

const PGFNPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState('leads');
  
  // Estado Leads (agregado)
  const [loadingLeads, setLoadingLeads] = useState(false);
  const [leads, setLeads] = useState<Lead[]>([]);
  const [totalLeads, setTotalLeads] = useState(0);
  const [totalLeadsReal, setTotalLeadsReal] = useState(0);
  const [facetsLeads, setFacetsLeads] = useState<any>({});
  
  // Estado Dívidas (individual)
  const [loadingDividas, setLoadingDividas] = useState(false);
  const [dividas, setDividas] = useState<Divida[]>([]);
  const [totalDividas, setTotalDividas] = useState(0);
  const [totalDividasReal, setTotalDividasReal] = useState(0);
  const [facetsDividas, setFacetsDividas] = useState<any>({});
  
  // Filtros Leads
  const [linhaFilter, setLinhaFilter] = useState<string[]>(['A', 'B']);
  const [ufFilter, setUfFilter] = useState<string[]>([]);
  const [faixaValorFilter, setFaixaValorFilter] = useState<string[]>([]);
  const [faixaTempoFilter, setFaixaTempoFilter] = useState<string[]>([]);
  const [valorMin, setValorMin] = useState<number | null>(null);
  const [valorMax, setValorMax] = useState<number | null>(null);
  const [apenasNaoAjuizadas, setApenasNaoAjuizadas] = useState(false);
  const [sortLeads, setSortLeads] = useState('valor_total:desc');
  
  // Filtros Dívidas
  const [searchQuery, setSearchQuery] = useState('');
  const [tipoDividaFilter, setTipoDividaFilter] = useState<string[]>([]);
  const [ajuizadoFilter, setAjuizadoFilter] = useState<string | null>(null);
  const [situacaoFilter, setSituacaoFilter] = useState<string[]>([]);
  const [tempoRange, setTempoRange] = useState<[number, number]>([0, 30]);
  const [receitaFilter, setReceitaFilter] = useState<string[]>([]);
  
  // Paginação
  const [offsetLeads, setOffsetLeads] = useState(0);
  const [offsetDividas, setOffsetDividas] = useState(0);
  const [limit] = useState(50);
  
  // Modal detalhes
  const [detailsVisible, setDetailsVisible] = useState(false);
  const [selectedLead, setSelectedLead] = useState<Lead | null>(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [dividasDoLead, setDividasDoLead] = useState<Divida[]>([]);

  const buscarLeads = async () => {
    setLoadingLeads(true);
    try {
      const response = await axios.post(API_SERVIDOR2.LEADS, {
        linha: linhaFilter.length > 0 ? linhaFilter : undefined,
        uf: ufFilter.length > 0 ? ufFilter : undefined,
        faixa_valor: faixaValorFilter.length > 0 ? faixaValorFilter : undefined,
        faixa_tempo: faixaTempoFilter.length > 0 ? faixaTempoFilter : undefined,
        alguma_ajuizada: apenasNaoAjuizadas ? false : undefined,
        offset: offsetLeads,
        limit,
        sort: sortLeads,
      });
      
      setLeads(response.data.leads || []);
      setTotalLeads(response.data.total || 0);
      setTotalLeadsReal(response.data.total_real || response.data.total || 0);
      setFacetsLeads(response.data.facets || {});
      message.success(`${response.data.leads?.length || 0} leads encontrados de ${(response.data.total_real || response.data.total || 0).toLocaleString()} total`);
    } catch (error) {
      console.error('Erro ao buscar leads:', error);
      message.error('Erro ao buscar leads PGFN');
    } finally {
      setLoadingLeads(false);
    }
  };

  const buscarDividas = async (query?: string) => {
    setLoadingDividas(true);
    try {
      const params: any = {
        q: query || searchQuery || '',
        limit,
        offset: offsetDividas,
        sort: 'valor_consolidado:desc',
      };
      
      // Usar parâmetros do novo backend (listas separadas por vírgula)
      if (tipoDividaFilter.length > 0) {
        params.tipos_divida = tipoDividaFilter.join(',');
      }
      
      if (ajuizadoFilter) {
        params.ajuizado = ajuizadoFilter;
      }
      
      if (situacaoFilter.length > 0) {
        params.situacoes = situacaoFilter.join(',');
      }
      
      if (ufFilter.length > 0) {
        params.ufs = ufFilter.join(',');
      }
      
      // Converter range de tempo para faixas
      const faixasTempo: string[] = [];
      if (tempoRange[0] <= 1 && tempoRange[1] >= 0) faixasTempo.push('ate_1ano');
      if (tempoRange[0] <= 2 && tempoRange[1] >= 1) faixasTempo.push('1_2anos');
      if (tempoRange[0] <= 3 && tempoRange[1] >= 2) faixasTempo.push('2_3anos');
      if (tempoRange[0] <= 5 && tempoRange[1] >= 3) faixasTempo.push('3_5anos');
      if (tempoRange[0] <= 10 && tempoRange[1] >= 5) faixasTempo.push('5_10anos');
      if (tempoRange[1] >= 10) faixasTempo.push('acima_10anos');
      
      if (faixasTempo.length > 0 && faixasTempo.length < 6) {
        params.faixas_tempo = faixasTempo.join(',');
      }
      
      const response = await axios.get(API_SERVIDOR2.PGFN_SEARCH, { params });
      
      setDividas(response.data.hits || []);
      setTotalDividas(response.data.total || 0);
      setTotalDividasReal(response.data.total_real || response.data.total || 0);
      setFacetsDividas(response.data.facets || {});
      message.success(`${response.data.hits?.length || 0} dívidas encontradas de ${(response.data.total_real || response.data.total || 0).toLocaleString()} total`);
    } catch (error) {
      console.error('Erro ao buscar dívidas:', error);
      message.error('Erro ao buscar dívidas PGFN');
    } finally {
      setLoadingDividas(false);
    }
  };

  const verDetalhesLead = async (lead: Lead) => {
    setSelectedLead(lead);
    setDetailsVisible(true);
    setLoadingDetails(true);
    
    try {
      // Buscar dívidas individuais deste CNPJ
      const response = await axios.get(API_SERVIDOR2.PGFN_SEARCH, {
        params: { q: lead.cnpj_basico, limit: 100 }
      });
      setDividasDoLead(response.data.hits || []);
    } catch (error) {
      console.error('Erro ao buscar detalhes:', error);
      message.error('Erro ao buscar detalhes do CNPJ');
    } finally {
      setLoadingDetails(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'leads') {
      buscarLeads();
    }
  }, [activeTab]);

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value);
  };

  const getLinhaColor = (linha: string) => {
    switch (linha) {
      case 'A': return 'green';
      case 'B': return 'orange';
      case 'C': return 'red';
      default: return 'default';
    }
  };

  const getTipoDividaColor = (tipo: string) => {
    switch (tipo) {
      case 'FGTS': return 'blue';
      case 'PREV': return 'purple';
      case 'SIDA': return 'cyan';
      default: return 'default';
    }
  };

  const columnsLeads = [
    {
      title: 'Linha',
      dataIndex: 'linha',
      key: 'linha',
      width: 70,
      render: (linha: string) => (
        <Tag color={getLinhaColor(linha)} style={{ fontWeight: 'bold' }}>
          {linha}
        </Tag>
      ),
    },
    {
      title: 'Score',
      dataIndex: 'score_risco',
      key: 'score_risco',
      width: 70,
      render: (score: number) => (
        <Tag color={score >= 80 ? 'green' : score >= 50 ? 'orange' : 'red'}>
          {score}
        </Tag>
      ),
    },
    {
      title: 'CNPJ',
      dataIndex: 'cnpj_completo',
      key: 'cnpj_completo',
      width: 160,
      render: (text: string) => (
        <span style={{ fontFamily: 'monospace' }}>
          {text?.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5')}
        </span>
      ),
    },
    {
      title: 'Devedor',
      dataIndex: 'nome_devedor',
      key: 'nome_devedor',
      ellipsis: true,
    },
    {
      title: 'Valor Total',
      dataIndex: 'valor_total',
      key: 'valor_total',
      width: 140,
      render: (value: number) => (
        <Text strong style={{ color: '#1890ff' }}>
          {formatCurrency(value)}
        </Text>
      ),
    },
    {
      title: 'Dívidas',
      key: 'dividas_info',
      width: 100,
      render: (_: any, record: Lead) => (
        <Space direction="vertical" size={0}>
          <Text>{record.total_dividas} total</Text>
          <Text type={record.qtd_ajuizadas > 0 ? 'danger' : 'secondary'} style={{ fontSize: 11 }}>
            {record.qtd_ajuizadas} ajuiz. / {record.qtd_nao_ajuizadas} não
          </Text>
        </Space>
      ),
    },
    {
      title: 'Tipos',
      key: 'tipos_divida',
      width: 130,
      render: (_: any, record: Lead) => (
        <Space size={2} wrap>
          {record.tem_fgts && <Tag color="blue">FGTS</Tag>}
          {record.tem_prev && <Tag color="purple">PREV</Tag>}
          {record.tem_sida && <Tag color="cyan">SIDA</Tag>}
        </Space>
      ),
    },
    {
      title: 'UF',
      dataIndex: 'uf',
      key: 'uf',
      width: 50,
    },
    {
      title: 'Tempo',
      key: 'tempo',
      width: 100,
      render: (_: any, record: Lead) => (
        <Space direction="vertical" size={0}>
          <Text type={record.tempo_divida_mais_antiga_anos > 10 ? 'danger' : 'secondary'}>
            {record.tempo_divida_mais_antiga_anos?.toFixed(1)}a (antiga)
          </Text>
          <Text type="secondary" style={{ fontSize: 11 }}>
            {record.tempo_divida_mais_recente_anos?.toFixed(1)}a (recente)
          </Text>
        </Space>
      ),
    },
    {
      title: 'Ações',
      key: 'actions',
      width: 90,
      render: (_: any, record: Lead) => (
        <Button
          type="primary"
          size="small"
          icon={<FileTextOutlined />}
          onClick={() => verDetalhesLead(record)}
        >
          Ver
        </Button>
      ),
    },
  ];

  const columnsDividas = [
    {
      title: 'Tipo',
      dataIndex: 'tipo_divida',
      key: 'tipo_divida',
      width: 80,
      render: (tipo: string) => (
        <Tag color={getTipoDividaColor(tipo)}>{tipo}</Tag>
      ),
    },
    {
      title: 'CNPJ/CPF',
      dataIndex: 'cnpj_cpf',
      key: 'cnpj_cpf',
      width: 150,
      render: (text: string) => (
        <span style={{ fontFamily: 'monospace', fontSize: 12 }}>{text}</span>
      ),
    },
    {
      title: 'Devedor',
      dataIndex: 'nome_devedor',
      key: 'nome_devedor',
      ellipsis: true,
    },
    {
      title: 'Valor',
      dataIndex: 'valor_consolidado',
      key: 'valor_consolidado',
      width: 130,
      render: (value: number) => (
        <Text strong style={{ color: '#1890ff' }}>
          {formatCurrency(value)}
        </Text>
      ),
    },
    {
      title: 'Receita',
      dataIndex: 'receita_principal',
      key: 'receita_principal',
      width: 150,
      ellipsis: true,
      render: (text: string) => (
        <Text style={{ fontSize: 11 }}>{text}</Text>
      ),
    },
    {
      title: 'Situação',
      dataIndex: 'situacao_inscricao',
      key: 'situacao_inscricao',
      width: 100,
      render: (text: string) => (
        <Tag color={text === 'INSCRITA' ? 'blue' : text === 'PARCELADA' ? 'green' : 'default'}>
          {text}
        </Tag>
      ),
    },
    {
      title: 'Ajuizado',
      dataIndex: 'indicador_ajuizado',
      key: 'indicador_ajuizado',
      width: 80,
      render: (text: string) => (
        <Tag color={text === 'SIM' ? 'red' : 'green'}>
          {text}
        </Tag>
      ),
    },
    {
      title: 'Data Insc.',
      dataIndex: 'data_inscricao',
      key: 'data_inscricao',
      width: 100,
    },
    {
      title: 'Tempo',
      dataIndex: 'tempo_inscricao_anos',
      key: 'tempo_inscricao_anos',
      width: 70,
      render: (tempo: number) => (
        <Text type={tempo > 10 ? 'danger' : tempo > 5 ? 'warning' : 'secondary'}>
          {tempo?.toFixed(1)}a
        </Text>
      ),
    },
    {
      title: 'UF',
      dataIndex: 'uf_devedor',
      key: 'uf_devedor',
      width: 50,
    },
  ];

  const renderFiltrosLeads = () => (
    <Card style={{ marginBottom: 16 }}>
      <Collapse
        defaultActiveKey={['1']}
        ghost
        items={[{
          key: '1',
          label: <span><FilterOutlined /> Filtros de Prospecção (Leads Agregados)</span>,
          children: (
            <Row gutter={[16, 16]}>
              <Col span={6}>
                <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>
                  Linha (Segmento):
                </label>
                <Select
                  mode="multiple"
                  style={{ width: '100%' }}
                  placeholder="Selecione"
                  value={linhaFilter}
                  onChange={setLinhaFilter}
                  options={[
                    { value: 'A', label: '🟢 Linha A - Alto potencial' },
                    { value: 'B', label: '🟡 Linha B - Médio potencial' },
                    { value: 'C', label: '🔴 Linha C - Baixo potencial' },
                  ]}
                />
              </Col>
              
              <Col span={6}>
                <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>
                  Estado (UF):
                </label>
                <Select
                  mode="multiple"
                  style={{ width: '100%' }}
                  placeholder="Todos"
                  value={ufFilter}
                  onChange={setUfFilter}
                  options={[
                    { value: 'SP', label: 'São Paulo' },
                    { value: 'RJ', label: 'Rio de Janeiro' },
                    { value: 'MG', label: 'Minas Gerais' },
                    { value: 'RS', label: 'Rio Grande do Sul' },
                    { value: 'PR', label: 'Paraná' },
                    { value: 'SC', label: 'Santa Catarina' },
                    { value: 'BA', label: 'Bahia' },
                    { value: 'PE', label: 'Pernambuco' },
                    { value: 'CE', label: 'Ceará' },
                    { value: 'GO', label: 'Goiás' },
                    { value: 'DF', label: 'Distrito Federal' },
                    { value: 'ES', label: 'Espírito Santo' },
                  ]}
                />
              </Col>
              
              <Col span={6}>
                <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>
                  Faixa de Valor:
                </label>
                <Select
                  mode="multiple"
                  style={{ width: '100%' }}
                  placeholder="Todas"
                  value={faixaValorFilter}
                  onChange={setFaixaValorFilter}
                  options={[
                    { value: 'acima_1M', label: 'Acima de R$ 1M' },
                    { value: '100k_1M', label: 'R$ 100k - R$ 1M' },
                    { value: '10k_100k', label: 'R$ 10k - R$ 100k' },
                    { value: 'ate_10k', label: 'Até R$ 10k' },
                  ]}
                />
              </Col>
              
              <Col span={6}>
                <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>
                  Faixa de Tempo (Oportunidade):
                </label>
                <Select
                  mode="multiple"
                  style={{ width: '100%' }}
                  placeholder="Todas"
                  value={faixaTempoFilter}
                  onChange={setFaixaTempoFilter}
                  options={[
                    { value: '2_3anos', label: '🎯 2-3 anos (Ideal)' },
                    { value: '3_5anos', label: '🟢 3-5 anos (Muito bom)' },
                    { value: '1_2anos', label: '🟡 1-2 anos (Bom)' },
                    { value: '5_10anos', label: '🟠 5-10 anos (Risco)' },
                    { value: 'ate_1ano', label: '⚪ Menos de 1 ano' },
                    { value: 'acima_10anos', label: '🔴 Acima de 10 anos' },
                  ]}
                />
              </Col>
              
              <Col span={6}>
                <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>
                  Status Ajuizamento:
                </label>
                <Switch
                  checked={apenasNaoAjuizadas}
                  onChange={setApenasNaoAjuizadas}
                  checkedChildren="Apenas não ajuizadas"
                  unCheckedChildren="Todas"
                />
              </Col>
              
              <Col span={6}>
                <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>
                  Ordenar por:
                </label>
                <Select
                  style={{ width: '100%' }}
                  value={sortLeads}
                  onChange={setSortLeads}
                  options={[
                    { value: 'valor_total:desc', label: '💰 Valor (maior)' },
                    { value: 'tempo_divida_mais_antiga_anos:asc', label: '⏰ Tempo (menor)' },
                    { value: 'total_dividas:desc', label: '📊 Qtd Dívidas (maior)' },
                  ]}
                />
              </Col>
              
              <Col span={6}>
                <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>
                  <DollarOutlined /> Valor Mínimo (R$):
                </label>
                <Space.Compact style={{ width: '100%' }}>
                  <InputNumber
                    style={{ width: '50%' }}
                    placeholder="Mínimo"
                    value={valorMin}
                    onChange={(v) => setValorMin(v)}
                    formatter={v => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                  />
                  <InputNumber
                    style={{ width: '50%' }}
                    placeholder="Máximo"
                    value={valorMax}
                    onChange={(v) => setValorMax(v)}
                    formatter={v => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                  />
                </Space.Compact>
              </Col>
              
              <Col span={12} style={{ display: 'flex', alignItems: 'flex-end' }}>
                <Button
                  type="primary"
                  icon={<SearchOutlined />}
                  onClick={buscarLeads}
                  loading={loadingLeads}
                  size="large"
                >
                  Buscar Leads
                </Button>
                <Button
                  icon={<ReloadOutlined />}
                  onClick={() => {
                    setLinhaFilter(['A', 'B']);
                    setUfFilter([]);
                    setFaixaValorFilter([]);
                    setFaixaTempoFilter([]);
                    setValorMin(null);
                    setValorMax(null);
                    setApenasNaoAjuizadas(false);
                    setSortLeads('valor_total:desc');
                  }}
                  style={{ marginLeft: 8 }}
                >
                  Limpar
                </Button>
              </Col>
            </Row>
          ),
        }]}
      />
    </Card>
  );

  const renderFiltrosDividas = () => (
    <Card style={{ marginBottom: 16 }}>
      <Space direction="vertical" style={{ width: '100%' }} size="middle">
        <Search
          placeholder="Buscar por nome, CNPJ, CPF..."
          enterButton={<><SearchOutlined /> Buscar</>}
          size="large"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          onSearch={buscarDividas}
          loading={loadingDividas}
        />
        
        <Collapse
          ghost
          items={[{
            key: '1',
            label: <span><FilterOutlined /> Filtros Avançados</span>,
            children: (
              <Row gutter={[16, 16]}>
                <Col span={6}>
                  <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>
                    Tipo de Dívida:
                  </label>
                  <Select
                    mode="multiple"
                    style={{ width: '100%' }}
                    placeholder="Todos"
                    value={tipoDividaFilter}
                    onChange={setTipoDividaFilter}
                    options={[
                      { value: 'FGTS', label: '💼 FGTS' },
                      { value: 'PREV', label: '🏛️ Previdenciário' },
                      { value: 'SIDA', label: '📋 Dívida Ativa' },
                    ]}
                  />
                </Col>
                
                <Col span={6}>
                  <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>
                    Ajuizado:
                  </label>
                  <Select
                    style={{ width: '100%' }}
                    placeholder="Todos"
                    value={ajuizadoFilter}
                    onChange={setAjuizadoFilter}
                    allowClear
                    options={[
                      { value: 'SIM', label: '⚖️ Sim - Em execução fiscal' },
                      { value: 'NAO', label: '✅ Não - Ainda não ajuizado' },
                    ]}
                  />
                </Col>
                
                <Col span={6}>
                  <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>
                    Situação:
                  </label>
                  <Select
                    mode="multiple"
                    style={{ width: '100%' }}
                    placeholder="Todas"
                    value={situacaoFilter}
                    onChange={setSituacaoFilter}
                    options={[
                      { value: 'INSCRITA', label: 'Inscrita' },
                      { value: 'PARCELADA', label: 'Parcelada' },
                      { value: 'SUSPENSA', label: 'Suspensa' },
                      { value: 'GARANTIDA', label: 'Garantida' },
                    ]}
                  />
                </Col>
                
                <Col span={6}>
                  <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>
                    Estado (UF):
                  </label>
                  <Select
                    mode="multiple"
                    style={{ width: '100%' }}
                    placeholder="Todos"
                    value={ufFilter}
                    onChange={setUfFilter}
                    options={[
                      { value: 'SP', label: 'SP' },
                      { value: 'RJ', label: 'RJ' },
                      { value: 'MG', label: 'MG' },
                      { value: 'RS', label: 'RS' },
                      { value: 'PR', label: 'PR' },
                      { value: 'SC', label: 'SC' },
                      { value: 'BA', label: 'BA' },
                    ]}
                  />
                </Col>
                
                <Col span={12}>
                  <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>
                    <ClockCircleOutlined /> Tempo de Inscrição (anos): {tempoRange[0]} - {tempoRange[1]}
                  </label>
                  <Slider
                    range
                    min={0}
                    max={30}
                    value={tempoRange}
                    onChange={(v) => setTempoRange(v as [number, number])}
                    marks={{
                      0: '0',
                      5: '5',
                      10: '10',
                      15: '15',
                      20: '20',
                      30: '30+',
                    }}
                  />
                </Col>
                
                <Col span={12} style={{ display: 'flex', alignItems: 'flex-end' }}>
                  <Button
                    type="primary"
                    icon={<SearchOutlined />}
                    onClick={() => buscarDividas()}
                    loading={loadingDividas}
                  >
                    Aplicar Filtros
                  </Button>
                  <Button
                    icon={<ReloadOutlined />}
                    onClick={() => {
                      setTipoDividaFilter([]);
                      setAjuizadoFilter(null);
                      setSituacaoFilter([]);
                      setTempoRange([0, 30]);
                      setUfFilter([]);
                    }}
                    style={{ marginLeft: 8 }}
                  >
                    Limpar
                  </Button>
                </Col>
              </Row>
            ),
          }]}
        />
      </Space>
    </Card>
  );

  const renderStatsLeads = () => {
    // Usar facets do backend para contagens reais
    const linhaFacets = facetsLeads.linha || {};
    const countA = linhaFacets['A'] || 0;
    const countB = linhaFacets['B'] || 0;
    const countC = linhaFacets['C'] || 0;
    
    return (
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col span={4}>
          <Card size="small" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
            <Statistic
              title={<span style={{ color: 'rgba(255,255,255,0.85)' }}>Total Leads</span>}
              value={totalLeadsReal}
              valueStyle={{ color: '#fff' }}
              prefix={<SearchOutlined />}
            />
          </Card>
        </Col>
        <Col span={4}>
          <Card size="small" style={{ borderLeft: '4px solid #52c41a' }}>
            <Statistic
              title="Linha A (Melhor)"
              value={countA}
              valueStyle={{ color: '#52c41a' }}
              prefix={<CheckCircleOutlined />}
              suffix={<Text type="secondary" style={{ fontSize: 12 }}>2-5 anos</Text>}
            />
          </Card>
        </Col>
        <Col span={4}>
          <Card size="small" style={{ borderLeft: '4px solid #faad14' }}>
            <Statistic
              title="Linha B (Bom)"
              value={countB}
              valueStyle={{ color: '#faad14' }}
              prefix={<ClockCircleOutlined />}
            />
          </Card>
        </Col>
        <Col span={4}>
          <Card size="small" style={{ borderLeft: '4px solid #ff4d4f' }}>
            <Statistic
              title="Linha C (Baixo)"
              value={countC}
              valueStyle={{ color: '#ff4d4f' }}
              prefix={<ExclamationCircleOutlined />}
              suffix={<Text type="secondary" style={{ fontSize: 12 }}>&gt;10 anos</Text>}
            />
          </Card>
        </Col>
        <Col span={8}>
          <Card size="small">
            <Statistic
              title="Valor Total (página atual)"
              value={leads.reduce((acc, l) => acc + l.valor_total, 0)}
              precision={2}
              prefix="R$"
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
      </Row>
    );
  };

  const tabItems = [
    {
      key: 'leads',
      label: (
        <span>
          <BankOutlined />
          Leads Agregados (por CNPJ)
        </span>
      ),
      children: (
        <>
          {renderFiltrosLeads()}
          {renderStatsLeads()}
          <Card>
            <Table
              columns={columnsLeads}
              dataSource={leads}
              rowKey="cnpj_completo"
              loading={loadingLeads}
              pagination={{
                total: totalLeadsReal,
                pageSize: limit,
                current: Math.floor(offsetLeads / limit) + 1,
                onChange: (page) => {
                  setOffsetLeads((page - 1) * limit);
                  setTimeout(buscarLeads, 0);
                },
                showTotal: (total, range) => `${range[0]}-${range[1]} de ${total.toLocaleString()} leads`,
                showSizeChanger: false,
              }}
              scroll={{ x: 1200 }}
            />
          </Card>
        </>
      ),
    },
    {
      key: 'dividas',
      label: (
        <span>
          <FileTextOutlined />
          Dívidas Individuais
        </span>
      ),
      children: (
        <>
          {renderFiltrosDividas()}
          <Card>
            <div style={{ marginBottom: 16 }}>
              <Text type="secondary">
                Total: <strong>{totalDividasReal.toLocaleString()}</strong> dívidas encontradas
              </Text>
            </div>
            <Table
              columns={columnsDividas}
              dataSource={dividas}
              rowKey="id"
              loading={loadingDividas}
              pagination={{
                total: totalDividasReal,
                pageSize: limit,
                current: Math.floor(offsetDividas / limit) + 1,
                onChange: (page) => {
                  setOffsetDividas((page - 1) * limit);
                  setTimeout(() => buscarDividas(), 0);
                },
                showTotal: (total, range) => `${range[0]}-${range[1]} de ${total.toLocaleString()} dívidas`,
                showSizeChanger: false,
              }}
              scroll={{ x: 1400 }}
            />
          </Card>
        </>
      ),
    },
    {
      key: 'bi',
      label: (
        <span>
          <PieChartOutlined />
          Dashboard BI
        </span>
      ),
      children: <PGFNDashboardBI />,
    },
  ];

  return (
    <div style={{ padding: '24px' }}>
      <Title level={3} style={{ marginBottom: 24 }}>
        <span style={{ 
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', 
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          fontWeight: 700 
        }}>
          NewWay NW
        </span>
        <Text style={{ marginLeft: 12, fontSize: 18, fontWeight: 400 }}>
          PGFN Intelligence - Dívidas Ativas da União
        </Text>
      </Title>
      
      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        items={tabItems}
        size="large"
      />
      
      <Modal
        title={
          <Space>
            <BankOutlined />
            Detalhes - {selectedLead?.nome_devedor}
          </Space>
        }
        open={detailsVisible}
        onCancel={() => {
          setDetailsVisible(false);
          setSelectedLead(null);
          setDividasDoLead([]);
        }}
        footer={null}
        width={1000}
      >
        {loadingDetails ? (
          <div style={{ textAlign: 'center', padding: 40 }}>
            <Spin size="large" />
          </div>
        ) : selectedLead && (
          <>
            <Descriptions bordered column={2} size="small">
              <Descriptions.Item label="CNPJ" span={2}>
                <Text copyable style={{ fontFamily: 'monospace' }}>
                  {selectedLead.cnpj_completo}
                </Text>
              </Descriptions.Item>
              <Descriptions.Item label="Nome">
                {selectedLead.nome_devedor}
              </Descriptions.Item>
              <Descriptions.Item label="UF">
                {selectedLead.ufs?.join(', ')}
              </Descriptions.Item>
              <Descriptions.Item label="Valor Total">
                <Text strong style={{ color: '#1890ff', fontSize: 16 }}>
                  {formatCurrency(selectedLead.valor_total)}
                </Text>
              </Descriptions.Item>
              <Descriptions.Item label="Score / Linha">
                <Space>
                  <Tag color={selectedLead.score_risco >= 80 ? 'green' : 'orange'}>
                    Score: {selectedLead.score_risco}
                  </Tag>
                  <Tag color={getLinhaColor(selectedLead.linha)}>
                    Linha {selectedLead.linha}
                  </Tag>
                </Space>
              </Descriptions.Item>
              <Descriptions.Item label="Total de Dívidas">
                {selectedLead.total_dividas}
              </Descriptions.Item>
              <Descriptions.Item label="Ajuizadas / Não Ajuizadas">
                <Text type="danger">{selectedLead.qtd_ajuizadas} ajuizadas</Text>
                {' / '}
                <Text type="success">{selectedLead.qtd_nao_ajuizadas} não ajuizadas</Text>
              </Descriptions.Item>
              <Descriptions.Item label="Tipos de Dívida" span={2}>
                {selectedLead.tem_fgts && <Tag color="blue">FGTS</Tag>}
                {selectedLead.tem_prev && <Tag color="purple">Previdenciário</Tag>}
                {selectedLead.tem_sida && <Tag color="cyan">Dívida Ativa</Tag>}
              </Descriptions.Item>
              <Descriptions.Item label="Dívida Mais Antiga">
                {selectedLead.tempo_divida_mais_antiga_anos?.toFixed(1)} anos
              </Descriptions.Item>
              <Descriptions.Item label="Dívida Mais Recente">
                {selectedLead.tempo_divida_mais_recente_anos?.toFixed(1)} anos
              </Descriptions.Item>
            </Descriptions>
            
            <Divider>Dívidas Individuais ({dividasDoLead.length})</Divider>
            
            <Table
              columns={columnsDividas}
              dataSource={dividasDoLead}
              rowKey="id"
              size="small"
              pagination={{ pageSize: 10 }}
              scroll={{ x: 1200 }}
            />
          </>
        )}
      </Modal>
    </div>
  );
};

export default PGFNPage;
